<script setup lang="ts">
    // @ts-expect-error - no types for autosize
    import autosize from 'autosize'
    import {
        computed,
        nextTick,
        onBeforeUnmount,
        onMounted,
        ref,
        useTemplateRef,
        watch,
    } from 'vue'
    import { useDropZone } from '@vueuse/core'
    import type { AgentFileUpload } from 'models'
    import type { PendingAttachment } from 'composables'
    import PkStreamingMarkdown from './PkStreamingMarkdown.vue'
    import PkChatbotFilePreview from './PkChatbotFilePreview.vue'
    import PkChatButton from './PkChatButton.vue'

    // --- Models & Props ---

    const modelValue = defineModel<string | undefined>({ default: undefined })
    const pendingAttachments = defineModel<PendingAttachment[]>(
        'pendingAttachments',
        { default: () => [] },
    )

    const emit = defineEmits<{
        submit: []
        'stop-generation': []
        'file-select': [file: File]
        'dismiss-attachment-notice': []
        'voice-start': []
    }>()

    const props = defineProps<{
        placeholder?: string
        dismissableNotice?: string
        status?: 'submitted' | 'streaming' | 'ready' | 'error'
        maxMessageLength?: number
        fileUpload?: AgentFileUpload
        enableDragDrop?: boolean
        attachmentLimitReached?: boolean
        attachmentNotice?: string
        /** Show the talk button: the agent has voice enabled. */
        voiceEnabled?: boolean
    }>()

    // --- Template refs ---

    const textareaEl = useTemplateRef<HTMLTextAreaElement>('textareaEl')
    const fileInputEl = useTemplateRef<HTMLInputElement>('fileInputEl')
    const cameraInputEl = useTemplateRef<HTMLInputElement>('cameraInputEl')
    const formEl = useTemplateRef<HTMLFormElement>('formEl')

    // --- Input buffering (decouples typing from reactive model propagation) ---

    const localValue = ref(modelValue.value ?? '')
    let syncTimer: ReturnType<typeof setTimeout> | null = null

    const cancelSync = () => {
        if (syncTimer !== null) {
            clearTimeout(syncTimer)
            syncTimer = null
        }
    }

    const flushSync = () => {
        cancelSync()
        modelValue.value = localValue.value
    }

    const scheduleSync = () => {
        cancelSync()
        syncTimer = setTimeout(() => {
            modelValue.value = localValue.value
            syncTimer = null
        }, 300)
    }

    const onTextareaInput = () => {
        localValue.value = textareaEl.value?.value ?? ''
        scheduleSync()
    }

    watch(modelValue, (v) => {
        if (v !== localValue.value) {
            localValue.value = v ?? ''
            nextTick(() => {
                if (textareaEl.value) {
                    autosize.update(textareaEl.value)
                }
            })
        }
    })

    // --- Status computeds ---

    const isStreaming = computed(() => props.status === 'streaming')
    const isSubmitted = computed(() => props.status === 'submitted')
    const isInputDisabled = computed(
        () => isStreaming.value || isSubmitted.value,
    )

    // --- Validation ---

    const currentLength = computed(() => localValue.value.length)

    const isOverLimit = computed(
        () =>
            props.maxMessageLength !== undefined &&
            currentLength.value > props.maxMessageLength,
    )

    const isUploadingAnyFile = computed(() =>
        pendingAttachments.value.some((a) => a.state === 'uploading'),
    )

    const isSubmitEnabled = computed(() => {
        if (
            isInputDisabled.value ||
            isUploadingAnyFile.value ||
            isOverLimit.value
        ) {
            return false
        }
        return localValue.value.trim().length > 0
    })

    // --- File upload ---

    const hasCaptureSupport = 'capture' in document.createElement('input')

    const acceptMimeTypes = computed(
        () => props.fileUpload?.allowedMimeTypes?.join(',') ?? '',
    )

    const acceptImageMimeTypes = computed(() =>
        (props.fileUpload?.allowedMimeTypes ?? [])
            .filter((m) => m.startsWith('image/'))
            .join(','),
    )

    const showCameraOption = computed(
        () => hasCaptureSupport && acceptImageMimeTypes.value.length > 0,
    )

    const openFilePicker = () => {
        fileInputEl.value?.click()
    }

    const openCameraPicker = () => {
        cameraInputEl.value?.click()
    }

    // The store owns the attachment limits and drops any overflow; the input
    // just forwards every picked/dropped file.
    const emitFiles = (files: File[]) => {
        for (const file of files) {
            emit('file-select', file)
        }
    }

    const onFileChange = (event: Event) => {
        const input = event.target as HTMLInputElement
        emitFiles(Array.from(input.files ?? []))
        input.value = ''
    }

    // --- Attachments ---

    const removeAttachment = (id: string) => {
        const att = pendingAttachments.value.find((a) => a.id === id)
        if (att) {
            URL.revokeObjectURL(att.localUrl)
        }
        pendingAttachments.value = pendingAttachments.value.filter(
            (a) => a.id !== id,
        )
    }

    // --- Drag & drop ---

    const isDragDropEnabled = computed(
        () => (props.enableDragDrop ?? true) && !!props.fileUpload?.enabled,
    )

    const handleDrop = (files: File[] | null) => {
        if (!isDragDropEnabled.value || !files) {
            return
        }
        emitFiles(files)
    }

    const { isOverDropZone } = useDropZone(formEl, {
        dataTypes: computed(() => props.fileUpload?.allowedMimeTypes ?? []),
        onDrop: handleDrop,
    })

    // --- Notice queue (single slot above the input, highest priority wins) ---

    type InputNotice = {
        id: string
        text?: string
        markdown?: string
        icon?: string
        dismiss?: () => void
    }

    const isDismissableNoticeVisible = ref(true)
    const closeDismissableNotice = () => {
        isDismissableNoticeVisible.value = false
    }

    const activeNotice = computed<InputNotice | null>(() => {
        // Order = priority. The alert takes precedence over the notice.
        if (props.attachmentNotice) {
            return {
                id: 'limit',
                icon: 'ri:information-line',
                text: props.attachmentNotice,
                dismiss: () => emit('dismiss-attachment-notice'),
            }
        }
        if (props.dismissableNotice && isDismissableNoticeVisible.value) {
            return {
                id: 'dismissable',
                markdown: props.dismissableNotice,
                dismiss: closeDismissableNotice,
            }
        }
        return null
    })

    // --- Submit ---

    const handleSubmit = () => {
        if (!isSubmitEnabled.value) {
            return
        }
        flushSync()
        emit('submit')
        localValue.value = ''
        modelValue.value = ''
        nextTick(() => {
            if (textareaEl.value) {
                textareaEl.value.value = ''
                autosize.update(textareaEl.value)
            }
        })
    }

    // --- Lifecycle ---

    onMounted(() => {
        if (textareaEl.value) {
            autosize(textareaEl.value)
        }
    })

    onBeforeUnmount(() => {
        cancelSync()
        if (textareaEl.value) {
            autosize.destroy(textareaEl.value)
        }
        for (const att of pendingAttachments.value) {
            URL.revokeObjectURL(att.localUrl)
        }
    })
</script>

<template>
    <div class="pk-chatbot-input">
        <div
            v-if="activeNotice"
            :key="activeNotice.id"
            class="pk-chatbot-input__notice"
            role="status">
            <VvIcon
                v-if="activeNotice.icon"
                :name="activeNotice.icon"
                class="pk-chatbot-input__notice-icon" />
            <PkStreamingMarkdown
                v-if="activeNotice.markdown"
                :markdown="activeNotice.markdown"
                class="wysiwyg pk-chatbot-input__notice-text" />
            <span v-else class="pk-chatbot-input__notice-text">
                {{ activeNotice.text }}
            </span>
            <VvButton
                v-if="activeNotice.dismiss"
                :title="$t('action.close')"
                modifiers="action-quiet"
                icon="ri:close-line"
                class="w-18 h-18"
                @click="activeNotice.dismiss()" />
        </div>
        <form
            ref="formEl"
            class="pk-chatbot-input__form"
            @submit.prevent="handleSubmit">
            <div
                v-if="isOverDropZone && isDragDropEnabled"
                class="pk-chatbot-input__drop-overlay">
                <VvIcon
                    name="ri:upload-cloud-2-line"
                    class="pk-chatbot-input__drop-overlay-icon" />
                <span>{{ $t('action.dropFile') }}</span>
            </div>
            <div
                v-if="pendingAttachments.length"
                class="pk-chatbot-input__attachments">
                <PkChatbotFilePreview
                    v-for="att in pendingAttachments"
                    :key="att.id"
                    :media-type="att.file.type"
                    :url="att.localUrl"
                    :filename="att.file.name"
                    :size="att.file.size"
                    :loading="att.state === 'uploading'"
                    :error="att.state === 'error' ? att.error : undefined"
                    :removable="att.state !== 'uploading'"
                    compact
                    @remove="removeAttachment(att.id)" />
            </div>
            <div class="pk-chatbot-input__fieldset">
                <slot name="prepend" />
                <VvDropdown
                    v-if="
                        fileUpload &&
                        fileUpload.enabled &&
                        (showCameraOption || $slots['additional-actions'])
                    "
                    v-bind="{
                        placement: 'top-start',
                        modifiers: 'menu',
                        flip: true,
                        offset: 3,
                    }">
                    <template #default="{ init, toggle, aria }">
                        <!-- The only one still written by hand: the dropdown
                         anchors itself to whatever `init` receives, and that
                         has to be the element, not a component instance -->
                        <button
                            :ref="(e: any) => init(e)"
                            v-bind="aria"
                            class="pk-chat-button"
                            type="button"
                            :title="$t('action.attachFile')"
                            :disabled="isInputDisabled"
                            @click="toggle">
                            <VvIcon
                                name="ri:add-line"
                                class="pk-chat-button__icon" />
                        </button>
                    </template>
                    <template #items>
                        <VvDropdownAction
                            v-if="showCameraOption"
                            :disabled="
                                isInputDisabled || attachmentLimitReached
                            "
                            @click="openCameraPicker">
                            <VvIcon
                                name="ri:camera-line"
                                class="pk-chatbot-input__dropdown-icon" />
                            {{ $t('action.takePhoto') }}
                        </VvDropdownAction>
                        <VvDropdownAction
                            :disabled="
                                isInputDisabled || attachmentLimitReached
                            "
                            @click="openFilePicker">
                            <VvIcon
                                name="ri:attachment-line"
                                class="pk-chatbot-input__dropdown-icon" />
                            {{ $t('action.attachFile') }}
                        </VvDropdownAction>
                        <slot name="additional-actions" />
                    </template>
                </VvDropdown>
                <PkChatButton
                    v-else-if="fileUpload && fileUpload.enabled"
                    icon="ri:add-line"
                    :title="$t('action.attachFile')"
                    :disabled="isInputDisabled || attachmentLimitReached"
                    @click="openFilePicker" />
                <input
                    v-if="fileUpload && fileUpload.enabled"
                    ref="fileInputEl"
                    type="file"
                    hidden
                    multiple
                    :accept="acceptMimeTypes"
                    @change="onFileChange" />
                <input
                    v-if="fileUpload && fileUpload.enabled && showCameraOption"
                    ref="cameraInputEl"
                    type="file"
                    capture="environment"
                    hidden
                    :accept="acceptImageMimeTypes"
                    @change="onFileChange" />
                <div class="pk-chatbot-input__content">
                    <textarea
                        ref="textareaEl"
                        :value="localValue"
                        class="pk-chatbot-input__textarea"
                        name="message"
                        rows="1"
                        :placeholder
                        @input="onTextareaInput"
                        @keydown.enter.prevent="handleSubmit"></textarea>
                </div>
                <div
                    v-if="maxMessageLength"
                    class="pk-chatbot-input__counter"
                    :class="{ 'pk-chatbot-input__counter--over': isOverLimit }">
                    {{ $n(currentLength, 'integer') }}/<span
                        class="opacity-60"
                        >{{ $n(maxMessageLength, 'integer') }}</span
                    >
                </div>
                <PkChatButton
                    v-if="voiceEnabled && !isStreaming"
                    icon="ri:mic-line"
                    :title="$t('action.talk')"
                    :disabled="isInputDisabled"
                    @click="emit('voice-start')" />
                <PkChatButton
                    v-if="isStreaming"
                    icon="ri:stop-circle-line"
                    :title="$t('action.stop')"
                    @click="emit('stop-generation')" />
                <PkChatButton
                    v-else
                    type="submit"
                    :icon="
                        isSubmitted
                            ? 'line-md:loading-loop'
                            : isSubmitEnabled
                              ? 'ri:send-ins-fill'
                              : 'ri:send-ins-line'
                    "
                    :title="
                        isSubmitEnabled
                            ? $t('action.submit')
                            : $t('label.disabled')
                    "
                    :disabled="!isSubmitEnabled" />
            </div>
        </form>
    </div>
</template>

<style lang="scss">
    .pk-chatbot-input {
        position: relative;
        display: flex;
        flex-direction: column;
        margin-inline: var(--spacing-sm);
        margin-bottom: var(--spacing-sm);

        &__notice {
            position: relative;
            padding: var(--spacing-10) var(--spacing-sm);
            display: flex;
            align-items: flex-start;
            gap: var(--spacing-sm);
            background-color: var(--color-surface-1);
            border-top-left-radius: var(--rounded-xl);
            border-top-right-radius: var(--rounded-xl);
            transition-property: var(--transition-property-all);
            transition-duration: var(--duration-300);
            transition-timing-function: var(--ease-in-out);
            font-size: var(--text-12);
            color: var(--color-word-4);

            &::after {
                content: '';
                display: block;
                position: absolute;
                left: 0;
                right: 0;
                bottom: calc(-1 * var(--spacing-12));
                height: var(--spacing-12);
                background-color: var(--color-surface-1);
                z-index: 1;
            }

            &-icon {
                flex-shrink: 0;
                width: var(--spacing-18);
                height: var(--spacing-18);
            }

            &-text {
                flex: 1;
                min-width: 0;
            }
        }

        &__form {
            position: relative;
            display: flex;
            z-index: 1;
            flex-direction: column;

            // The composer is a field too: same border and focus ring as the
            // form fields, on top of the shadow and the 12px radius that make
            // it the container (12) of its 8px buttons
            border-radius: var(--rounded-xl);
            border: 1px solid var(--input-border-color);
            background-color: var(--color-surface);
            padding: var(--spacing-14);
            box-shadow: var(--shadow-lg);
            font-size: var(
                --text-16
            ); // prevent ios zoom on input focus by using a font size >= 16px
            gap: var(--spacing-xs);
            transition: var(--input-transition);

            @include media-breakpoint-up('sm', $breakpoints) {
                font-size: var(--text-14);
                padding: var(--spacing-10);
            }

            &:has(:focus-within) {
                border-color: var(--input-focus-color);
                box-shadow: var(--shadow-lg), var(--input-focus-ring);
            }
        }

        &__drop-overlay {
            position: absolute;
            inset: 0;
            z-index: 10;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: var(--spacing-xs);
            border-radius: var(--rounded-xl);
            background-color: color-mix(
                in srgb,
                var(--color-surface) 85%,
                transparent
            );
            pointer-events: none;
        }

        &__fieldset {
            display: flex;
            align-items: center;
            gap: var(--spacing-xs);
            flex: 1;
        }

        &__content {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: var(--spacing-4);
            min-width: 0;
            overflow: hidden;
        }

        &__textarea {
            display: block;
            border: none;
            outline: none;
            resize: none;
            width: 100%;
            max-height: 200px;
            color: var(--color-word-1);
            background-color: var(--color-surface);

            &::placeholder {
                color: var(--color-word-4);
            }
        }

        &__attachments {
            display: flex;
            flex-wrap: wrap;
            // Roomy gap so the uniform-height chips breathe in the row.
            gap: var(--spacing-12);
            padding-bottom: var(--spacing-xs);
            border-bottom: 1px solid var(--color-surface-2);
        }

        &__counter {
            background-color: var(--color-surface-1);
            border: 1px solid var(--color-surface-3);
            border-radius: var(--rounded-md);
            font-size: var(--text-12);
            color: var(--color-word-3);
            line-height: 1;
            padding-inline: var(--spacing-6);
            padding-block: var(--spacing-4);
            z-index: 1;
            font-variant-numeric: tabular-nums;
            font-family: var(--font-mono);
            font-weight: var(--font-bold);

            &--over {
                color: var(--color-danger);
                border-color: var(--color-danger-lighten-5);
                font-weight: var(--font-bold);
            }
        }
    }
</style>
